import { Injectable } from '@angular/core';

import { EmployeesService } from '../../../gt2/api/employees.service';

/**
 * Alimente la barre verticale d'avatars (chat-panel) avec les vrais
 * employés/utilisateurs du tenant.
 *
 * NOTE: la présence (pastilles de couleur) et le compteur de messages
 * non lus (puces) sont pour l'instant générés aléatoirement — ils seront
 * branchés prochainement sur de vrais statuts en temps réel.
 */
@Injectable({
  providedIn: 'root',
})
export class ChatPanelService {
  contacts?: any[];

  // Statuts de présence supportés par le template/SCSS (pastilles).
  private readonly _statuses: string[] = [
    'online',
    'away',
    'do-not-disturb',
    'offline',
  ];

  constructor(private _employeesService: EmployeesService) {}

  /**
   * Charge les employés et les transforme en contacts pour le rail.
   *
   * @returns {Promise<any[]>}
   */
  loadContacts(): Promise<any[]> {
    return this._employeesService
      .getEmployeesForPicker()
      .then((employees: any[]) => {
        this.contacts = (employees ?? [])
          .filter((employee) => this._isUser(employee))
          .map((employee) => this._toContact(employee));
        return this.contacts;
      })
      .catch(() => {
        this.contacts = [];
        return this.contacts;
      });
  }

  /**
   * Ne garder que les employés qui possèdent un compte utilisateur.
   * ListEmployeeTransformer expose user_uuid à plat (pas de sous-objet user).
   */
  private _isUser(employee: any): boolean {
    return (
      employee?.is_user === true ||
      !!employee?.user_uuid ||
      !!employee?.user?.uuid
    );
  }

  /**
   * Transforme un employé en contact affichable par le rail.
   */
  private _toContact(employee: any): any {
    const name: string =
      employee.full_name ||
      employee.name ||
      [employee.first_name, employee.last_name].filter(Boolean).join(' ') ||
      '—';

    return {
      id: employee.uuid ?? employee.slug ?? employee.id,
      // UUID du compte User — c'est lui qu'attend ChatStore.createDirect().
      userUuid: employee.user_uuid ?? employee.user?.uuid ?? null,
      name,
      initials: this._initialsOf(name),
      avatar: employee.avatar_thumb || employee.avatar || '',
      // Données random en attendant le temps réel :
      status: this._randomStatus(), // présence (pastille de couleur)
      unread: this._randomUnread(), // messages non lus (puce)
    };
  }

  /**
   * Initiales (2 premières lettres des 2 premiers mots).
   */
  private _initialsOf(name: string): string {
    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  }

  /**
   * Statut de présence aléatoire (temporaire).
   */
  private _randomStatus(): string {
    return this._statuses[Math.floor(Math.random() * this._statuses.length)];
  }

  /**
   * Compteur de messages non lus aléatoire (temporaire) : ~1 contact sur 3
   * possède un badge, avec une valeur entre 1 et 5.
   */
  private _randomUnread(): number | undefined {
    return Math.random() < 0.35 ? Math.floor(Math.random() * 5) + 1 : undefined;
  }
}
